import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FeatureToggle } from '@Enums';
import { FeatureManagerService } from '@Services/global/feature-manager/feature-manager.service';
import { BaseService } from '@Services';
import { CaseRequirementResponse, NoteResponse, RequirementResponse } from '@Underwriting/models';
import { AppService } from 'app/app.service';
import { IConfigService } from 'app/config/iconfigservice';
import moment from 'moment';
import { EMPTY, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, finalize, map, shareReplay, switchMap, take, tap } from 'rxjs/operators';
import { SubSink } from 'subsink';

/// DEV NOTE: https://stackoverflow.com/a/33676723/253564
// The above SO as helpful in working towards what I'm trying to achieve with this service.

@Injectable({
    providedIn: 'root'
})
export default class UWRequirementsDataService extends BaseService {
    private readonly featureManagerService = inject(FeatureManagerService);
    // Requirements State
    private requirementsLoaded = false;
    private accountIdForLoadedRequirements = '';
    private requirementsSub = new ReplaySubject<RequirementResponse[]>(1);
    public requirements$ = this.requirementsSub.asObservable();
    private requirementsRequests = new Map<string, Observable<RequirementResponse[]>>();
    // Case Requirements State
    private caseRequirementsLoaded = false;
    private caseIdForLoadedCaseRequirements = '';
    private caseRequirementsSub = new ReplaySubject<CaseRequirementResponse[]>(1);
    public caseRequirements$ = this.caseRequirementsSub.asObservable();
    private caseRequirementsRequests = new Map<string, Observable<CaseRequirementResponse[]>>();
    // Notes State
    private notesLoaded = false;
    private caseIdForLoadedNotes = '';
    private notesSub = new ReplaySubject<NoteResponse[]>(1);
    public notes$ = this.notesSub.asObservable();
    private notesRequests = new Map<string, Observable<NoteResponse[]>>();


    private subs = new SubSink();

    constructor(
        public configService: IConfigService,
        public httpClient: HttpClient,
        public appService: AppService
    ) {
        super(appService, configService, httpClient);
    }

    //#region Helpers

    private requirementsAreLoaded = (caseId: string) => caseId == this.accountIdForLoadedRequirements && this.requirementsLoaded;
    private caseRequirementsAreLoaded = (caseId: string) => caseId == this.caseIdForLoadedCaseRequirements && this.caseRequirementsLoaded;
    private notesAreLoaded = (caseId: string) => caseId == this.caseIdForLoadedNotes && this.notesLoaded;

    // DEV NOTE: instead of having each method need a `clientCode: string` parameter, I would like to refactor client data into a
    // `client.data.service.ts` that's responsible for loading / caching the current client context. Anyone component that needs client data
    // just needs to inject that service.  Maybe this should be put into `AppService`?.
    // There's already a similar concept going on in this code-base. See `app-routing.module.ts` and look for `Resolvers.ClientResolverService`.
    // Certain routes have client resolvers. The `ClientResolverService` injects  `clientDataService`. The `clientDataService` loads all the clients
    // and is pretty similar to the new 'data.service' concept (see: this service).  The resolver gets all the clients and finds the one that matches
    // the route `clientCode` parameter. If then makes this client available in the `ActivatedRoute` services's `data` property. (See: `case-summary.component.ts`)
    // This would be a similar concept I'm considering. Except, instead of putting resolvers on routes that have to be remembered to be configured,
    // the "new" ClientsDataService will load all clients (ie. same as old one but using BaseService) and then having a helper method that
    // uses a router service that gets the current route params and if there's a `ClientCode`, return the "current client" object.
    // The goal would be, at some point, this could be updated to return a "global client parameter" instead of a route.  Eg. The site would be updated
    // to require a client to be picked before continuing (similar to picking your local e-commerce store).
    private baseUrl = (clientCode: string) => `${this.baseUwwApiUrl}/v2/client/${clientCode}/`;

    // Starts (or reuses) the GET that publishes into the state subject; callers return the long-lived state stream.
    private reloadWithClientToggle<T>(
        url: string,
        requests: Map<string, Observable<T[]>>,
        load: () => Observable<T[]>,
        publish: (result: T[]) => void,
        reuseInFlight = true
    ): void {
        const clientId = this.appService.getClientId('');
        const enabled$ = clientId
            ? this.featureManagerService.isEnabled(FeatureToggle.ConfigWorkbenchDeduplicateApiCalls, clientId).pipe(
                catchError(error => {
                    this.appService.showResponseErrorMsg(error);
                    return of(false);
                })
            )
            : of(false);
        const request = enabled$.pipe(
            take(1),
            switchMap(deduplicate => {
                const existingRequest = deduplicate && reuseInFlight && requests.get(url);
                if (existingRequest) return existingRequest;

                if (!deduplicate) return load().pipe(tap(publish));

                const sharedRequest = load().pipe(
                    tap(result => {
                        if (requests.get(url) === sharedRequest) publish(result);
                    }),
                    finalize(() => {
                        if (requests.get(url) === sharedRequest) requests.delete(url);
                    }),
                    shareReplay({ bufferSize: 1, refCount: false })
                );
                requests.set(url, sharedRequest);
                return sharedRequest;
            }),
            shareReplay({ bufferSize: 1, refCount: false })
        );
        // Eagerly start the request; HTTP completes on its own, so the subscription isn't retained.
        request.pipe(catchError(() => EMPTY)).subscribe();
    }

    //#endregion
    //#region Lifecycle

    ngOnDestroy() {
        this.subs.unsubscribe();
    }

    //#endregion
    //#region Requirements

    public reloadRequirements(clientCode: string, accountId: string, hideWaiting = false) {
        return this.requestRequirements(clientCode, accountId, hideWaiting, false);
    }

    private requestRequirements(clientCode: string, accountId: string, hideWaiting: boolean, reuseInFlight: boolean) {
        const url = `${this.baseUrl(clientCode)}Requirements/account/${accountId}`;
        this.reloadWithClientToggle(
            url,
            this.requirementsRequests,
            () => super.getData<RequirementResponse[]>(url, null, hideWaiting),
            requirements => {
                requirements.sort((a, b) => {
                    if (a.sortOrder < b.sortOrder) return -1;
                    if (a.sortOrder > b.sortOrder) return 1;
                    if (a.name < b.name) return -1;
                    if (a.name > b.name) return 1;
                    return 0;
                });
                this.requirementsSub.next(requirements);
                this.requirementsLoaded = true;
                this.accountIdForLoadedRequirements = accountId;
            },
            reuseInFlight
        );
        return this.requirements$;
    }

    public loadRequirements(clientCode: string, accountId: string) {
        if (this.requirementsAreLoaded(accountId)) return this.requirements$;

        const response = this.requestRequirements(clientCode, accountId, false, true);
        return response;
    }

    //#endregion
    //#region Case Requirements

    public reloadCaseRequirements(clientCode: string, caseId: string, hideWaiting = false) {
        return this.requestCaseRequirements(clientCode, caseId, hideWaiting, false);
    }

    private requestCaseRequirements(clientCode: string, caseId: string, hideWaiting: boolean, reuseInFlight: boolean) {
        const url = `${this.baseUrl(clientCode)}CaseRequirements/case/${caseId}`;
        this.reloadWithClientToggle(
            url,
            this.caseRequirementsRequests,
            () => super.getData<CaseRequirementResponse[]>(url, null, hideWaiting),
            caseRequirements => {
                this.caseRequirementsSub.next(caseRequirements);
                this.caseRequirementsLoaded = true;
                this.caseIdForLoadedCaseRequirements = caseId;
            },
            reuseInFlight
        );
        return this.caseRequirements$;
    }

    public loadCaseRequirements(clientCode: string, caseId: string) {
        if (this.caseRequirementsAreLoaded(caseId)) return this.caseRequirements$;

        const response = this.requestCaseRequirements(clientCode, caseId, false, true);
        return response;
    }

    //#endregion
    //#region Notes

    // NOTE: split this into it's own data service or rename this data service to "UWWorkenchDataService" instead of being specific to "Requirements"
    public reloadNotes(clientCode: string, caseId: string, hideWaiting = false) {
        return this.requestNotes(clientCode, caseId, hideWaiting, false);
    }

    private requestNotes(clientCode: string, caseId: string, hideWaiting: boolean, reuseInFlight: boolean) {
        const url = `${this.baseUrl(clientCode)}Notes/case/${caseId}`;
        this.reloadWithClientToggle(
            url,
            this.notesRequests,
            () => super.getData<NoteResponse[]>(url, null, hideWaiting).pipe(
            map(notes => {
                const sortedNotes = [...notes];
                sortedNotes.sort((a: NoteResponse, b: NoteResponse) => {
                    const first = moment(a.audit.createdOn);
                    const second = moment(b.audit.createdOn);

                    return second.diff(first, 'seconds');
                });
                return sortedNotes;
            })
            ),
            notes => {
                notes.forEach(note => {
                    note.createdOnDisplay = moment.utc(note.audit.createdOn).tz('America/Chicago').format("M/D/YYYY h:mm a");
                    if (note.audit.updatedOn)
                    {
                        note.updatedOnDisplay = moment.utc(note.audit.updatedOn).tz('America/Chicago').format("M/D/YYYY h:mm a");
                    }
                });

                this.notesSub.next(notes);
                this.notesLoaded = true;
                this.caseIdForLoadedNotes = caseId;
            },
            reuseInFlight
        );
        return this.notes$;
    }

    public loadNotes(clientCode: string, caseId: string) {
        if (this.notesAreLoaded(caseId)) return this.notes$;

        const response = this.requestNotes(clientCode, caseId, false, true);
        return response;
    }

    //#endregion

    public load(clientCode: string, accountId: string, caseId: string) {
        this.loadRequirements(clientCode, accountId);
        this.loadCaseRequirements(clientCode, caseId);
        this.loadNotes(clientCode, caseId);
    }
}
